import React from 'react'

import clsx from 'clsx'

import Image from 'next/image'

import { BookOpenIcon } from 'src/common/components/icons/BookOpenIcon'
import { BriefCaseIcon } from 'src/common/components/icons/BriefCaseIcon'
import { CheckIcon } from 'src/common/components/icons/CheckIcon'
import { DollarIcon } from 'src/common/components/icons/DollarIcon'
import { LocationIcon } from 'src/common/components/icons/LocationIcon'
import { ResumeIcon } from 'src/common/components/icons/ResumeIcon'
import {
  EnumTypographyVariants,
  Typography,
} from 'src/common/components/Typography/Typography'
import { Maybe } from 'src/graphql/types'

import s from './CandidateResumeInfoFull.module.scss'

type GetListItemsProps = Pick<
  CandidateResumeInfoFullProps,
  'experience' | 'englishLevel'
>

const getEngLevel = (englishLevel: string) => {
  switch (englishLevel) {
    case 'B2':
      return 'Intermediate'
    case 'C1':
      return 'Advanced'
    case 'C2':
      return 'Proficient'
  }
  return ''
}

const getListItems = ({ experience, englishLevel }: GetListItemsProps) => {
  const output = [
    experience && {
      text: experience,
      icon: <BriefCaseIcon />,
    },
    {
      text: 'Developer location: unknown location',
      icon: <LocationIcon />,
      isBlured: true,
    },
    {
      text: 'Availability: Ready to start now',
      icon: <CheckIcon />,
    },
    {
      text: 'Full time - 40 hours per week',
      icon: <ResumeIcon />,
    },
    englishLevel && {
      text: `English level: ${getEngLevel(englishLevel)}`,
      icon: <BookOpenIcon />,
    },
    {
      text: `Developer price range: unknown price`,
      icon: <DollarIcon />,
      isBlured: true,
    },
  ].filter(Boolean)

  return output as Array<{
    text: string
    icon: JSX.Element
    isBlured?: boolean
  }>
}

export type CandidateResumeInfoFullProps = {
  firstName: Maybe<string> | undefined
  lastName: Maybe<string> | undefined
  photo: Maybe<string> | undefined
  specialization: Maybe<string> | undefined
  experience: Maybe<string> | undefined
  overlap: Maybe<string> | undefined
  price: Maybe<string> | undefined
  englishLevel: Maybe<string> | undefined
  renderButtons: (className?: string) => React.ReactNode
}

export const CandidateResumeInfoFull: React.FC<
  CandidateResumeInfoFullProps
> = ({
  experience,
  firstName,
  lastName,
  photo,
  specialization,
  englishLevel,
  renderButtons,
}) => {
  const renderImg = (mediaClass: string) => (
    <div className={clsx(s.CandidateResumeInfoFull__avatar, mediaClass)}>
      {!!photo && (
        <Image
          src={photo}
          layout="fill"
          objectFit="cover"
          alt=""
          quality={100}
          unoptimized
        />
      )}
    </div>
  )

  return (
    <section className={s.CandidateResumeInfoFull}>
      {renderImg(s.CandidateResumeInfoFull__desktop)}

      <div className={s.CandidateResumeInfoFull__content}>
        <div className={s.CandidateResumeInfoFull__header}>
          {renderImg(s.CandidateResumeInfoFull__mobile)}

          <div className={s.CandidateResumeInfoFull__headerInner}>
            <div className={s.CandidateResumeInfoFull__titleWrapper}>
              <Typography
                text={(firstName || '') + ' ' + (lastName || '')}
                variant={EnumTypographyVariants.H2F}
                className={s.CandidateResumeInfoFull__title}
              />

              <div className={s.CandidateResumeInfoFull__desktopLg}>
                {renderButtons()}
              </div>
            </div>
            {!!specialization && (
              <Typography
                variant={EnumTypographyVariants.P2F}
                text={specialization}
                as="div"
              />
            )}
          </div>
        </div>
        <ul className={s.CandidateResumeInfoFull__list}>
          {getListItems({
            experience,
            englishLevel,
          }).map(({ text, icon, isBlured }, i) => (
            <Typography
              key={i}
              variant={EnumTypographyVariants.P2F}
              className={clsx(
                s.CandidateResumeInfoFull__listItem,
                isBlured && s.CandidateResumeInfoFull__blur
              )}
              as="li"
            >
              <div className={s.CandidateResumeInfoFull__icon}>{icon}</div>
              {text}
            </Typography>
          ))}
        </ul>

        <div className={s.CandidateResumeInfoFull__mobile}>
          {renderButtons(s.CandidateResumeInfoFull__btnsMobile)}
        </div>
      </div>
    </section>
  )
}
